import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import styles from '../css/pc/PcGeneral.module.css';
import '../css/pc/AccountBindingByCookieTab.css';
import '../css/pc/AccountBindingBySsoTab.css';
import classNames from 'classnames/bind';
import CancelButton from './buttons/CancelButton';
import { 
  bindingAccount, 
  updateTransactionStatus, 
  getTransactionResult,
  getPaymentCredentials
} from '../libs/GsmpiApi';
import { TRANSACTION_STATUS, POLLING_TIME, POST_MESSAGE_CONTENT_MESSAGE } from '../common/constants';
import debug from 'debug';

const cx = classNames.bind(styles);
const log = debug('app:AccountBindingBySsoTab');

export default function AccountBindingBySsoTab() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { serviceId, transactionId, domains } = useSelector((state) => state.paymentClient);
  const browserId = useSelector((state) => state.browserDetails.browserId);
  const { userDetails }  = useSelector((state) => state.userDetailsResponse);
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(POLLING_TIME.EMAIL_POLL);
  const abortControllersRef = useRef([]);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);

        getTransactionResult(transactionId, false, abortSignal)
          .then(result => {
            handlePollingResult(result);
          })
          .catch(error => {
            handlePollingError(error);
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  const handlePollingResult = (result) => {
    if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
      cancelPollingRequests();
      handleConfirmedTransaction(result.data.refId);
      setStatusPollingCountDown(0);
    } else if (result.data.status === TRANSACTION_STATUS.EXPIRED) {
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (result.data.status === TRANSACTION_STATUS.CANCELED) {
      navigate('/gsmpi-web/cancel');
    } else if (result.data.status === TRANSACTION_STATUS.ERRED) {
      navigate('/gsmpi-web/error');
    } else {
      log(`result status : ${result.data.status}`);
    }
  };

  const handlePollingError = (error) => {
    if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
      //transaction is expired
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
      //transaction is invalid
      navigate('/gsmpi-web/error');
    } else {
      navigate('/gsmpi-web/error');
    }
  };

  function handleConfirmedTransaction(refId) {
    getPaymentCredentials({refId: refId, serviceId: serviceId})
      .then((result) => {
        const state = {
          state: {
            paymentCredential: result.data,
            referenceId: refId,
          }
        };

        navigate('/gsmpi-web/done', state);
      })
      .catch(() => navigate('/gsmpi-web/error'));
  }

  const renderDeviceList = () => {
    let deviceListMap = [];

    userDetails.deviceList.forEach(device => {
      if (device.deviceNickName) {
        deviceListMap.push(<div className="mpi-requestpay-account" lang={i18n.language}>{device.deviceNickName}</div>);
      } else {
        deviceListMap.push(<div className="mpi-requestpay-account" lang={i18n.language}>{device.deviceModelName}</div>);
      }    
    });

    return deviceListMap;
  };

  async function bindAccountRequest({request, params}) {

    await bindingAccount(params.transactionId, request)
      .catch((error) => {
        throw error;
      });

    log('done bindingAccount api call');

    return {
      transactionId: params.transactionId,
      email: request.saEmail,
      useCookie: false,
      useAccount: true
    };
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    const requestWithParams = {
      request: {
        serviceId: serviceId,
        saEmail: userDetails.encryptedEmail,
        browserId: browserId,
        domains: domains,
        useCookie: false,
        useAccount: true
      },
      params: {
        transactionId: transactionId
      }
    };
    
    bindAccountRequest(requestWithParams)
      .then(result => navigate('/gsmpi-web/time-remaining', {state: result}))
      .catch(error => {
        if (error.resultCode === 'OPM2N1101' || error.resultCode === 'OPM1N1039') {  //transaction is expired
          navigate('/gsmpi-web/timeout');
        } else if (error.resultCode === 'OPM2N1103' || error.resultCode === 'OPM2N1104') {  //transaction is invalid
          navigate('/gsmpi-web/error');
        } else if (error.resultCode === 'OPM1N1041') {  //retry count exceeded
          const cancelPayload = {
            payment: {
              status: TRANSACTION_STATUS.EXPIRED,
              reason: 'Exceeded retry count'
            },
            timestamp: new Date().getTime()
          };

          updateTransactionStatus(transactionId, cancelPayload)
            .then(() => {
              navigate('/gsmpi-web/cancel');
            });
        } else {
          navigate('/gsmpi-web/error');
        }
      });
  };

  return (
    <>
      <div className={cx('mpi-content')}>
        <div className="mpi-cookie-requestpay" lang={i18n.language}>
          <p className={cx('mpi-requestpay-text')} lang={i18n.language}>{t('pay.gsmpi.withCookie.noti1')}</p>
          <div className="mpi-requestpay-account">{userDetails.maskedEmail}</div>
          {renderDeviceList()}
        </div>
      </div>
      <div className={cx('mpi-boxbottom')}>
        <div className={`${cx('mpi-button')} ${cx('is-clearfix')}`}>
          <CancelButton className={cx('mpi-button-cancel')} transactionId={transactionId} />
          <button type="button" className={cx('mpi-button-submit')} onClick={handleSubmit}>
            {t('pay.gsmpi.withCookie.button2')}
          </button>
        </div>
      </div>
    </>
  );
}